import { useState, useEffect } from "react";

import { QuestionCircleOutlined } from "@ant-design/icons";
import { Form, Button, Divider, Tooltip, Row, Col, Radio } from "antd";
import { t } from "i18next";
import { useNavigate } from "react-router-dom";

import useCreateFlowConfiguration from "../../../api/hooks/useCreateFlowConfiguration";
import useFlowRateActions from "../../../api/hooks/useFlowRateActions";
import useFlowRateRange from "../../../api/hooks/useFlowRateRange";
import { useAvailableDeviceSerialNumberOptionsContext } from "../../../context/FdsFiltersValuesProvider";
import useWindowSize from "../../../hooks/useWindowSize";
import DurationTimePicker from "../DurationTimePicker/DurationTimePicker";
import FdsSchedulesListFilters from "../Filters/FdsSchedulesListFilters";
import "./CreateFlowConfiguration.scss";

function CreateFlowConfiguration() {
  const DurationTimePickerId = {
    Low: "LOW",
    Medium: "MEDIUM",
    High: "HIGH",
  };
  const window = useWindowSize();
  const navigate = useNavigate();
  const { data: flowRateRange } = useFlowRateRange();
  const [disabledCreate, setDisabledCreate] = useState(true);

  const [rangeValidLow, setRangeValidLow] = useState(true);
  const [rangeValidMedium, setRangeValidMedium] = useState(true);
  const [rangeValidHigh, setRangeValidHigh] = useState(true);

  const tooltipText = t("flow_configuration.define_flow");
  const [optionsFlowActions, setOptionsFlowActions] = useState([]);
  const flowRateActions = useFlowRateActions();
  const labelActionWhenDetectedDefault = useState(
    t("flow_configuration.action"),
  );
  const [labelActionWhenDetected, setLabelActionWhenDetected] = useState(
    labelActionWhenDetectedDefault,
  );
  const [isFlowRateActionsDisabled, setIsFlowRateActionsDisabled] =
    useState(true);
  const [action, setAction] = useState("0");
  const [actionOnLowFlowRate, setActionOnLowFlowRate] = useState("0");
  const [actionOnMedFlowRate, setActionOnMedFlowRate] = useState("0");
  const [actionOnHighFlowRate, setActionOnHighFlowRate] = useState("0");
  const [selectedFlowRateType, setSelectedFlowRateType] = useState("");
  const [lowFlowRateDuration, setLowFlowRateDuration] = useState("00:00");
  const [medFlowRateDuration, setMedFlowRateDuration] = useState("00:00");
  const [highFlowRateDuration, setHighFlowRateDuration] = useState("00:00");
  const [isFlowRateInputClickCaptured, setIsFlowRateInputClickCaptured] =
    useState(false);

  const {
    availableDeviceSerialNumberOptions,
    availableServiceUniqueIdOptions,
  } = useAvailableDeviceSerialNumberOptionsContext();
  const { mutate: createFlowConfiguration, isLoading: isCreating } =
    useCreateFlowConfiguration();

  useEffect(() => {
    const transformedArray = Array.isArray(flowRateActions.data)
      ? flowRateActions.data.map((item) => ({
          label: item.Name,
          value: item.Value.toString(),
        }))
      : [];
    setOptionsFlowActions(transformedArray);
  }, [flowRateActions.data]);

  const timeStringToMinutes = (strValue) => {
    let result = 0;
    const indexOfColon = strValue.indexOf(":");
    if (indexOfColon === -1) return result;
    const parts = strValue.split(":");
    if (parts.length !== 2) return result;
    result = parseInt(parts[0], 10) * 60 + parseInt(parts[1], 10);
    return result;
  };

  useEffect(() => {
    if (flowRateRange) {
      const lowDurationInMinutes = timeStringToMinutes(lowFlowRateDuration);
      const medDurationInMinutes = timeStringToMinutes(medFlowRateDuration);
      const highDurationInMinutes = timeStringToMinutes(highFlowRateDuration);

      const isLowValid =
        lowDurationInMinutes >= flowRateRange.FromMinutes &&
        lowDurationInMinutes <= flowRateRange.ToMinutes;

      const isMedValid =
        medDurationInMinutes >= flowRateRange.FromMinutes &&
        medDurationInMinutes <= flowRateRange.ToMinutes;

      const isHighValid =
        highDurationInMinutes >= flowRateRange.FromMinutes &&
        highDurationInMinutes <= flowRateRange.ToMinutes;

      setRangeValidLow(isLowValid);
      setRangeValidMedium(isMedValid);
      setRangeValidHigh(isHighValid);

      if (isLowValid && isMedValid && isHighValid) {
        setDisabledCreate(false);
      } else if (!isLowValid || !isMedValid || !isHighValid) {
        setDisabledCreate(true);
      }
    }
  }, [
    availableDeviceSerialNumberOptions,
    lowFlowRateDuration,
    medFlowRateDuration,
    highFlowRateDuration,
    flowRateRange,
  ]);

  const handleFlowRateSave = () => {
    const systemUniqueIds = [];
    availableServiceUniqueIdOptions.forEach((option) => {
      if (option.value === "all") return;
      systemUniqueIds.push(option.value);
    });

    const flowConfigs = [];
    availableDeviceSerialNumberOptions.forEach((o) => {
      const option = JSON.parse(o);

      if (option.value === "all") return;

      const flowConfig = {
        DeviceSerialNumber: option.value,
        LowDuration: lowFlowRateDuration,
        LowAction: actionOnLowFlowRate,
        MediumDuration: medFlowRateDuration,
        MediumAction: actionOnMedFlowRate,
        HighDuration: highFlowRateDuration,
        HighAction: actionOnHighFlowRate,
      };
      flowConfigs.push(flowConfig);
    });

    if (flowConfigs && systemUniqueIds) {
      const model = {
        UpdateFlowConfigurations: flowConfigs,
        SelectedSystemUniqueIds: systemUniqueIds,
      };
      createFlowConfiguration(model);
    }

    navigate("/settings/flow-configuration");
  };

  const onActionChange = ({ target: { value } }) => {
    switch (selectedFlowRateType) {
      case DurationTimePickerId.Low:
        setActionOnLowFlowRate(value);
        break;
      case DurationTimePickerId.Medium:
        setActionOnMedFlowRate(value);
        break;
      case DurationTimePickerId.High:
        setActionOnHighFlowRate(value);
        break;
      default:
        break;
    }

    setAction(value);
  };

  const handleLowFlowRateOnClick = (id) => {
    setAction(actionOnLowFlowRate);
    setLabelActionWhenDetected(t("flow_configuration.action_low"));
    setIsFlowRateActionsDisabled(false);
    setIsFlowRateInputClickCaptured(true);
    setSelectedFlowRateType(id);
  };

  const handleMediumFlowRateOnClick = (id) => {
    setAction(actionOnMedFlowRate);
    setLabelActionWhenDetected(t("flow_configuration.action_medium"));
    setIsFlowRateActionsDisabled(false);
    setIsFlowRateInputClickCaptured(true);
    setSelectedFlowRateType(id);
  };

  const handleHighFlowRateOnClick = (id) => {
    setAction(actionOnHighFlowRate);
    setLabelActionWhenDetected(t("flow_configuration.action_high"));
    setIsFlowRateActionsDisabled(false);
    setIsFlowRateInputClickCaptured(true);
    setSelectedFlowRateType(id);
  };

  const handleLowFlowRateOnChange = (newValue) => {
    setLowFlowRateDuration(newValue);
  };

  const handleMediumFlowRateOnChange = (newValue) => {
    setMedFlowRateDuration(newValue);
  };

  const handleHighFlowRateOnChange = (newValue) => {
    setHighFlowRateDuration(newValue);
  };

  const handleNoControlClick = () => {
    if (isFlowRateInputClickCaptured) {
      setLabelActionWhenDetected(labelActionWhenDetectedDefault);
      setIsFlowRateActionsDisabled(true);
      setIsFlowRateInputClickCaptured(false);
    }
  };

  return (
    <Form
      disabled={isCreating}
      className="create-new-schedule h-full flex flex-col"
      role="presentation"
      onClick={handleNoControlClick}
    >
      <div className="schedule-header">
        {window?.width > 992 && (
          <div className="flex items-center">
            <h1 className="page-title mb-0">
              {t("flow_configuration.create_flow_configuration")}
            </h1>
            <Tooltip
              placement="top"
              title={tooltipText}
              className="ml-4 text-triple-white"
            >
              <QuestionCircleOutlined />
            </Tooltip>
          </div>
        )}

        <Button
          size="large"
          type="primary"
          htmlType="submit"
          className="mb-2"
          disabled={disabledCreate}
          onClick={handleFlowRateSave}
        >
          {t("flow_configuration.create_new")}
        </Button>
      </div>

      <div className="mb-8">
        <div
          role="presentation"
          onKeyDown={() => {}}
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          <Form.Item>
            <FdsSchedulesListFilters />
          </Form.Item>
        </div>
        <Divider orientation="left" orientationMargin={0} />
      </div>
      <div className="mb-8">
        {window?.width > 992 && (
          <div className="flex items-center">
            <h2 className="section-title mb-8">
              {t("flow_configuration.flow_rate")}
            </h2>
          </div>
        )}
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={24} md={6} lg={6} xl={6}>
            <div className="flex items-center">
              <span className="control-title mb-2">
                {t("flow_configuration.low")}
                <Tooltip
                  placement="top"
                  title={t("flow_configuration.tooltip_low")}
                  className="ml-4 text-triple-white"
                >
                  <QuestionCircleOutlined />
                </Tooltip>
              </span>
            </div>
            <div
              role="presentation"
              onKeyDown={() => {}}
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              <Form.Item>
                <DurationTimePicker
                  id={DurationTimePickerId.Low}
                  handleOnClickCallback={handleLowFlowRateOnClick}
                  handleOnChangeCallback={handleLowFlowRateOnChange}
                  rangeValid={rangeValidLow}
                />
              </Form.Item>
            </div>
          </Col>
          <Col xs={24} sm={24} md={6} lg={6} xl={6}>
            <div className="flex items-center">
              <span className="control-title mb-2">
                {t("flow_configuration.medium")}
                <Tooltip
                  placement="top"
                  title={t("flow_configuration.tooltip_medium")}
                  className="ml-4 text-triple-white"
                >
                  <QuestionCircleOutlined />
                </Tooltip>
              </span>
            </div>
            <div
              role="presentation"
              onKeyDown={() => {}}
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              <Form.Item>
                <DurationTimePicker
                  id={DurationTimePickerId.Medium}
                  handleOnClickCallback={handleMediumFlowRateOnClick}
                  handleOnChangeCallback={handleMediumFlowRateOnChange}
                  rangeValid={rangeValidMedium}
                />
              </Form.Item>
            </div>
          </Col>
          <Col xs={24} sm={24} md={6} lg={6} xl={6}>
            <div className="flex items-center">
              <span className="control-title mb-2">
                {t("flow_configuration.high")}
                <Tooltip
                  placement="top"
                  title={t("flow_configuration.tooltip_high")}
                  className="ml-4 text-triple-white"
                >
                  <QuestionCircleOutlined />
                </Tooltip>
              </span>
            </div>
            <div
              role="presentation"
              onKeyDown={() => {}}
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              <Form.Item>
                <DurationTimePicker
                  id={DurationTimePickerId.High}
                  handleOnClickCallback={handleHighFlowRateOnClick}
                  handleOnChangeCallback={handleHighFlowRateOnChange}
                  rangeValid={rangeValidHigh}
                />
              </Form.Item>
            </div>
          </Col>
        </Row>
        <Divider orientation="left" orientationMargin={0} />
      </div>
      <div className="mb-8">
        {window?.width > 992 && (
          <div className="flex items-center">
            <h2 className="section-title mb-8">{labelActionWhenDetected}</h2>
          </div>
        )}
        <div
          className="flex items-center radio-group-container"
          role="presentation"
          onKeyDown={() => {}}
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          <Form.Item>
            <Radio.Group
              options={optionsFlowActions}
              onChange={onActionChange}
              value={action}
              optionType="button"
              buttonStyle="solid"
              defaultValue="0"
              size="medium"
              disabled={isFlowRateActionsDisabled}
            />
          </Form.Item>
        </div>
      </div>
    </Form>
  );
}

export default CreateFlowConfiguration;
